import { PageController } from '@client/components/_core/layout/PageController';
import { VDropdown } from '@client/components/_shared/forms/view-form/VDropdown';
import { VEntityOwnerSelect } from '@client/components/_shared/forms/view-form/VEntityOwnerSelect';
import { VTextInput } from '@client/components/_shared/forms/view-form/VTextInput';
import { VTipTapInput } from '@client/components/_ui/data-entry/TipTap';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { ControlActivities } from '@client/components/controls/ControlActivities';
import { ControlTests } from '@client/components/controls/ControlTests';
import { LinkedRegisterRecordsPanel } from '@client/components/registers/LinkedRegisterRecordsPanel';
import { TaskList } from '@client/components/tasks/TaskList';
import { useCreateSignalStore } from '@client/context/signal';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrg } from '@client/hooks/business/org/useOrg';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFeatureFlag } from '@client/hooks/useFeatureFlag';
import { trpc } from '@client/trpc/client';
import type { ShareableRouteContext } from '@client/types/router';
import { requiredFieldRule } from '@client/utils';
import {
    CONTROL_FREQUENCY,
    CONTROL_STATUSES,
    CONTROL_TYPES,
    type ControlFrequency,
    type ControlStatus,
    type ControlType,
} from '@shared/constants';
import { hasPermission } from '@shared/permissions/roles';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Card, Col, Row, Skeleton } from 'antd';
import { useMemo } from 'react';
import { match } from 'ts-pattern';
import { ControlContext, type ControlSignals } from '../../ControlContext';
import { Obligations } from './Obligations';
import { Risks } from './Risks';
import { controlFormStore } from './store';

export const Route = createFileRoute('/console/$orgId/controls/register/details/$id')({
    staticData: {
        ui: {
            subtitle:
                'The Risk Control Management page allows you to define, assign, and monitor internal controls to mitigate risks and support organizational compliance.',
        },
        entity: 'control',
    },
    params: {
        parse({ id }) {
            return {
                id: BigInt(id),
            };
        },
    },
    beforeLoad(ctx): ShareableRouteContext {
        async function title() {
            const control = await ctx.context.trpcUtils.control.getControl.ensureData({ id: ctx.params.id });
            return `Control: ${control.name || control.id}`;
        }

        async function breadcrumb() {
            const control = await ctx.context.trpcUtils.control.getControl.ensureData({ id: ctx.params.id });
            return control.name || String(control.id);
        }

        return {
            ui: {
                title,
            },
            breadcrumb: {
                title: breadcrumb,
            },
        };
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ControlDetails />
        </PageController>
    );
}

function ControlDetails() {
    const orgId = useOrgId();
    const signalStore = useCreateSignalStore<ControlSignals>();
    const { id } = Route.useParams();
    const updateControlMutation = trpc.control.updateControl.useMutation();
    const { trpcUtils } = useTrpc();
    const navigate = useNavigate();
    const { enabledModules } = useOrg();
    const user = useUser();
    const isRiskFeatureEnabled = enabledModules.includes('risk');
    // CUR-644 REC-L2: the reverse Linked-records panel needs register:read on
    // top of control access (dual-permission — the server re-checks both).
    const registersEnabled = useFeatureFlag('enable-registers', false);
    const showLinkedRegisterRecords =
        registersEnabled && enabledModules.includes('compliance') && hasPermission(user.roles, 'register:read');

    // Fetch control data with tRPC query
    const controlQuery = trpc.control.getControl.useQuery(
        { id },
        {
            enabled: !!id,
            throwOnError(error) {
                message.destroy();

                match(error.data?.errorId)
                    .with('recordNotFound', () => {
                        void errorMessage
                            .showAsync(
                                "The control you are trying to access either doesn't exist or you don't have permissions to access it.",
                            )
                            .then(() => {
                                navigate({
                                    to: '/console/$orgId/controls/register',
                                    params: { orgId },
                                });
                            });
                    })
                    .otherwise(() => {
                        errorMessage.show(error);
                    });

                return false;
            },
        },
    );

    const { formData: control } = controlFormStore.useInit({
        entity: 'control',
        data: controlQuery.data,
        isLoading: controlQuery.isLoading,

        async onReload() {
            await controlQuery.refetch();
        },

        async onSave(currentValue, newValue) {
            try {
                await updateControlMutation.mutateAsync({
                    id: currentValue.id,
                    set: newValue,
                });

                await trpcUtils.control.invalidate();

                return { success: true };
            } catch (e) {
                return { success: false, error: e };
            }
        },
    });

    const canEdit = useMemo(() => {
        if (!control) {
            return false;
        }

        const hasOrgAdminPermission = hasPermission(user.roles, 'org:admin');
        const hasUpdateOwnHandlingPartiesPermission =
            hasPermission(user.roles, 'control:update:own_handling_parties') &&
            control.handlingPartyIds?.some((item) => user.handlingPartyIds.includes(item));
        const hasUpdateIsOwnerPermission =
            hasPermission(user.roles, 'control:update:is_owner') && control.ownerId === user.id;

        return hasOrgAdminPermission || hasUpdateIsOwnerPermission || hasUpdateOwnHandlingPartiesPermission;
    }, [control, user]);

    const VEntityOwnerSelectInstance = VEntityOwnerSelect({
        entity: 'control',
        formStore: controlFormStore,
        isReadOnly: !canEdit,
    });

    return (
        <ControlContext.Provider value={signalStore}>
            {id === control?.id ? (
                <Row gutter={[16, 16]}>
                    <Col xs={24} lg={16}>
                        <Row gutter={[16, 16]}>
                            <Col span={24}>
                                <Card>
                                    <Row gutter={[16, 16]}>
                                        <Col xs={24} sm={24} md={8}>
                                            <VDropdown
                                                label="Status"
                                                field="status"
                                                rules={requiredFieldRule}
                                                formatValueFn={(value) => CONTROL_STATUSES[value as ControlStatus]}
                                                options={Object.keys(CONTROL_STATUSES).map((key) => ({
                                                    label: CONTROL_STATUSES[key as ControlStatus],
                                                    value: key,
                                                }))}
                                                formStore={controlFormStore}
                                                isReadOnly={!canEdit}
                                            />
                                        </Col>
                                        <Col xs={24} sm={24} md={8}>
                                            <VDropdown
                                                label="Frequency"
                                                field="frequency"
                                                rules={requiredFieldRule}
                                                formStore={controlFormStore}
                                                isReadOnly={!canEdit}
                                                formatValueFn={(value) => CONTROL_FREQUENCY[value as ControlFrequency]}
                                                options={Object.keys(CONTROL_FREQUENCY).map((key) => ({
                                                    label: CONTROL_FREQUENCY[key as ControlFrequency],
                                                    value: key,
                                                }))}
                                            />
                                        </Col>
                                        <Col xs={24} sm={24} md={8}>
                                            <VDropdown
                                                label="Type"
                                                field="type"
                                                rules={requiredFieldRule}
                                                formStore={controlFormStore}
                                                isReadOnly={!canEdit}
                                                formatValueFn={(value) => CONTROL_TYPES[value as ControlType]}
                                                options={Object.keys(CONTROL_TYPES).map((key) => ({
                                                    label: CONTROL_TYPES[key as ControlType],
                                                    value: key,
                                                }))}
                                            />
                                        </Col>
                                        <Col xs={24} sm={24} md={16}>
                                            <VEntityOwnerSelectInstance.HandlingParty />
                                        </Col>
                                        <Col xs={24} sm={24} md={8}>
                                            <VEntityOwnerSelectInstance.Owner helpMessage={control.ownerHint || ''} />
                                        </Col>

                                        <Col xs={24}>
                                            <VTextInput
                                                label="Control name"
                                                field="name"
                                                rules={requiredFieldRule}
                                                formStore={controlFormStore}
                                                isReadOnly={!canEdit}
                                            />
                                        </Col>
                                        <Col xs={24}>
                                            <VTextInput
                                                label="Control objective"
                                                field="objective"
                                                rules={requiredFieldRule}
                                                formStore={controlFormStore}
                                                isReadOnly={!canEdit}
                                            />
                                        </Col>
                                        <Col xs={24}>
                                            <VTipTapInput
                                                field="description"
                                                label="Control description"
                                                rules={requiredFieldRule}
                                                formStore={controlFormStore}
                                                isReadOnly={!canEdit}
                                                maxRowsToDisplay={12}
                                            />
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                            <Col span={24}>
                                <ControlActivities controlId={id} readOnly={!canEdit} />
                            </Col>
                            <Col span={24}>
                                <ControlTests controlId={id} readOnly={!canEdit} />
                            </Col>
                        </Row>
                    </Col>
                    <Col xs={24} lg={8}>
                        <Row gutter={[16, 16]}>
                            <Col xs={24}>
                                <Obligations controlId={control.id} readonly={!canEdit} />
                            </Col>
                            {isRiskFeatureEnabled && (
                                <Col xs={24}>
                                    <Risks controlId={control.id} readOnly={!canEdit} />
                                </Col>
                            )}
                            {showLinkedRegisterRecords && (
                                <Col xs={24}>
                                    <LinkedRegisterRecordsPanel targetType="control" targetId={control.id} />
                                </Col>
                            )}
                            <Col xs={24}>
                                <TaskList
                                    hideAddTask={!canEdit}
                                    relatedEntity="control"
                                    relatedEntityId={control.id}
                                    size="small"
                                />
                            </Col>
                        </Row>
                    </Col>
                </Row>
            ) : (
                <Skeleton active />
            )}
            <Outlet />
        </ControlContext.Provider>
    );
}
